// Путь: D:\QR\QR-WEB\dashboard\src\components\Auth\VerifyEmail.js
// Версия: 1.0.0
// Дата: 02.12.2025

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const VerifyEmail = ({ onLogin }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  const email = location.state?.email || localStorage.getItem('pendingEmail') || '';

  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index, value) => {
    if (value.length > 1) {
      // Вставка из буфера
      const chars = value.slice(0, 6).split('');
      const newCode = [...code];
      chars.forEach((char, i) => {
        if (index + i < 6) newCode[index + i] = char;
      });
      setCode(newCode);
      const nextIndex = Math.min(index + chars.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      setError('Введите 6-значный код');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/auth/verify-email`, {
        email: email,
        code: fullCode
      });

      if (res.data.success) {
        setSuccess('Email подтвержден!');
        
        localStorage.setItem('authToken', res.data.data.user);
        localStorage.setItem('userId', res.data.data.user.id);
        localStorage.setItem('userEmail', res.data.data.user.email);
        localStorage.setItem('userRole', res.data.data.user.role);
        localStorage.removeItem('pendingEmail');

        if (onLogin) onLogin(res.data.data.user);

        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Неверный код');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setResendLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/auth/resend-code`, { email });
      setSuccess('Код отправлен повторно!');
      setCountdown(60);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка отправки');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-info">
        <div className="auth-info-content">
          <div className="brand">
            <span className="brand-icon">📧</span>
            <h1 className="brand-name">DARY</h1>
            <p className="brand-tagline">Подтверждение Email</p>
          </div>
          
          <div className="features">
            <h2>Проверьте вашу почту</h2>
            <p className="features-desc">
              Мы отправили 6-значный код на<br/>
              <strong>{email}</strong>
            </p>
            
            <ul className="features-list">
              <li>
                <span className="feature-icon">📬</span>
                <div>
                  <strong>Проверьте Входящие</strong>
                  <span>Код придёт в течение минуты</span>
                </div>
              </li>
              <li>
                <span className="feature-icon">📁</span>
                <div>
                  <strong>Проверьте Спам</strong>
                  <span>Иногда письма попадают туда</span>
                </div>
              </li>
              <li>
                <span className="feature-icon">⏱️</span>
                <div>
                  <strong>Код действует 10 минут</strong>
                  <span>После истечения запросите новый</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="auth-form-section">
        <div className="auth-card">
          <div className="auth-card-header">
            <h2>Введите код</h2>
            <p>6-значный код из письма</p>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="code-inputs">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={e => handleChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  className="code-input"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? '⏳ Проверка...' : '✓ Подтвердить'}
            </button>
          </form>

          <div className="resend-section">
            <p>Не получили код?</p>
            <button 
              onClick={handleResend} 
              disabled={resendLoading || countdown > 0}
              className="btn-resend"
            >
              {resendLoading ? '⏳ Отправка...' : 
               countdown > 0 ? `Повторить через ${countdown}с` : 
               '📧 Отправить повторно'}
            </button>
          </div>

          <p className="auth-footer">
            Неверный email? <a href="/register">Изменить</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
